/**
 * Photo Panel Component
 * Sidebar showing list of photos with thumbnails, filters, sorting, and management
 */

import { useState, useMemo } from 'react';
import type { PhotoData, PhotoCategory, PhotoSortField, PhotoSortOrder } from '../../../../shared/types/project';
import { DEFAULT_PHOTO_CATEGORIES } from '../../../../shared/types/project';
import { CategoryIconDisplay } from './CategoryManagerModal';
import { LocalImage } from '../../../components/common/LocalImage';

interface PhotoPanelProps {
  photos: PhotoData[];
  customCategories: PhotoCategory[];
  selectedPhotoId?: string;
  onSelectPhoto: (photoId: string) => void;
  onEditPhoto: (photo: PhotoData) => void;
  onDeletePhoto: (photoId: string) => void;
  onFlyToPhoto: (photo: PhotoData) => void;
  onViewPhoto: (photo: PhotoData) => void;
  onManageCategories: () => void;
  onAddPhotoAtLocation?: (coordinates: { longitude: number; latitude: number }) => void;
}

export function PhotoPanel({
  photos,
  customCategories,
  selectedPhotoId,
  onSelectPhoto,
  onEditPhoto,
  onDeletePhoto,
  onFlyToPhoto,
  onViewPhoto,
  onManageCategories,
  onAddPhotoAtLocation,
}: PhotoPanelProps) {
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterArea, setFilterArea] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<PhotoSortField>('addedAt');
  const [sortOrder, setSortOrder] = useState<PhotoSortOrder>('desc');
  const [showFilters, setShowFilters] = useState(false);

  const allCategories = useMemo(
    () => [...DEFAULT_PHOTO_CATEGORIES, ...customCategories],
    [customCategories]
  );

  const getCategoryById = (id: string): PhotoCategory | undefined => {
    return allCategories.find(c => c.id === id);
  };

  // Get unique values from photos for filter dropdowns
  const uniqueTypes = useMemo(() => {
    const types = new Set(photos.map(p => p.photoType).filter(Boolean));
    return Array.from(types);
  }, [photos]);

  const uniqueAreas = useMemo(() => {
    const areas = new Set(photos.map(p => p.photoArea).filter(Boolean));
    return Array.from(areas);
  }, [photos]);

  const uniqueLevels = useMemo(() => {
    const levels = new Set(photos.map(p => p.level).filter(Boolean));
    return Array.from(levels);
  }, [photos]);

  const filteredAndSortedPhotos = useMemo(() => {
    // Filter
    let result = photos.filter(photo => {
      const matchesCategory = filterCategory === 'all' || photo.categoryId === filterCategory;
      const matchesType = filterType === 'all' || photo.photoType === filterType;
      const matchesArea = filterArea === 'all' || photo.photoArea === filterArea;
      const matchesLevel = filterLevel === 'all' || photo.level === filterLevel;
      const matchesSearch = !searchTerm ||
        photo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photo.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photo.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesType && matchesArea && matchesLevel && matchesSearch;
    });

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'addedAt':
          comparison = new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime();
          break;
        case 'timestamp':
          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          comparison = aTime - bTime;
          break;
        case 'category':
          const aCat = getCategoryById(a.categoryId)?.name || '';
          const bCat = getCategoryById(b.categoryId)?.name || '';
          comparison = aCat.localeCompare(bCat);
          break;
        case 'type':
          comparison = (a.photoType || '').localeCompare(b.photoType || '');
          break;
        case 'area':
          comparison = (a.photoArea || '').localeCompare(b.photoArea || '');
          break;
        case 'level':
          comparison = (a.level || '').localeCompare(b.level || '');
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [photos, filterCategory, filterType, filterArea, filterLevel, searchTerm, sortField, sortOrder, getCategoryById]);

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const activeFilterCount = [
    filterCategory !== 'all',
    filterType !== 'all',
    filterArea !== 'all',
    filterLevel !== 'all',
  ].filter(Boolean).length;

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterType('all');
    setFilterArea('all');
    setFilterLevel('all');
    setSearchTerm('');
  };

  return (
    <div className="photo-panel">
      <div className="photo-panel__header">
        <span className="photo-panel__title">Photos</span>
        <span className="photo-panel__count">{filteredAndSortedPhotos.length}/{photos.length}</span>
      </div>

      {/* Search */}
      <div className="photo-panel__search">
        <svg
          className="photo-panel__search-icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="text"
          placeholder="Search photos..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="photo-panel__search-input"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="photo-panel__search-clear"
            title="Clear search"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Sort & Filter Controls */}
      <div className="photo-panel__controls">
        {/* Sort */}
        <div className="photo-panel__sort">
          <select
            value={sortField}
            onChange={e => setSortField(e.target.value as PhotoSortField)}
            className="photo-panel__sort-select"
            title="Sort by"
          >
            <option value="addedAt">Date Added</option>
            <option value="timestamp">Date Taken</option>
            <option value="name">Name</option>
            <option value="category">Category</option>
            <option value="type">Type</option>
            <option value="area">Area</option>
            <option value="level">Level</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="photo-panel__sort-order"
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sortOrder === 'asc' ? (
                <path d="M12 19V5m0 0l-7 7m7-7l7 7" />
              ) : (
                <path d="M12 5v14m0 0l7-7m-7 7l-7-7" />
              )}
            </svg>
          </button>
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`photo-panel__filter-toggle ${showFilters || activeFilterCount > 0 ? 'photo-panel__filter-toggle--active' : ''}`}
          title="Filters"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
          </svg>
          {activeFilterCount > 0 && (
            <span className="photo-panel__filter-badge">{activeFilterCount}</span>
          )}
        </button>

        {/* Manage Categories */}
        <button
          className="photo-panel__manage-btn"
          onClick={onManageCategories}
          title="Manage Categories"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="photo-panel__filters">
          <div className="photo-panel__filter-row">
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="photo-panel__filter-select"
            >
              <option value="all">All Categories</option>
              {allCategories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {uniqueTypes.length > 0 && (
            <div className="photo-panel__filter-row">
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="photo-panel__filter-select"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          )}

          {uniqueAreas.length > 0 && (
            <div className="photo-panel__filter-row">
              <select
                value={filterArea}
                onChange={e => setFilterArea(e.target.value)}
                className="photo-panel__filter-select"
              >
                <option value="all">All Areas</option>
                {uniqueAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
          )}

          {uniqueLevels.length > 0 && (
            <div className="photo-panel__filter-row">
              <select
                value={filterLevel}
                onChange={e => setFilterLevel(e.target.value)}
                className="photo-panel__filter-select"
              >
                <option value="all">All Levels</option>
                {uniqueLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
          )}

          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="photo-panel__clear-filters"
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Photo List */}
      <div className="photo-panel__list">
        {filteredAndSortedPhotos.length === 0 ? (
          <div className="photo-panel__empty">
            {photos.length === 0 ? (
              <>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                <p>No photos yet</p>
                <span>Click "Add Photo" to add geolocated photos</span>
              </>
            ) : (
              <>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <p>No photos match</p>
                <span>Try adjusting your filters</span>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="button"
                    style={{ marginTop: '8px', fontSize: 'var(--font-xs)' }}
                  >
                    Clear Filters
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          filteredAndSortedPhotos.map(photo => {
            const category = getCategoryById(photo.categoryId);
            const isSelected = photo.id === selectedPhotoId;

            return (
              <div
                key={photo.id}
                className={`photo-panel__item ${isSelected ? 'photo-panel__item--selected' : ''}`}
                onClick={() => {
                  onSelectPhoto(photo.id);
                  onFlyToPhoto(photo);
                }}
                onDoubleClick={() => {
                  onViewPhoto(photo);
                }}
                title="Double-click to view hi-res photo"
              >
                {/* Thumbnail */}
                <div className="photo-panel__thumbnail">
                  {photo.thumbnailPath ? (
                    <LocalImage
                      src={photo.thumbnailPath}
                      alt={photo.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  )}
                  {photo.isManuallyPlaced && (
                    <div className="photo-panel__manual-badge" title="Manually placed">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="photo-panel__info">
                  <div className="photo-panel__name">{photo.name}</div>
                  <div className="photo-panel__meta">
                    {category && (
                      <span
                        className="photo-panel__category"
                        style={{ color: category.color }}
                      >
                        <CategoryIconDisplay icon={category.icon} size={12} />
                        {category.name}
                      </span>
                    )}
                  </div>
                  <div className="photo-panel__meta-secondary">
                    {photo.photoType && <span>{photo.photoType}</span>}
                    {photo.level && <span>{photo.level}</span>}
                    {!photo.photoType && !photo.level && (
                      <span className="photo-panel__date">
                        {formatDate(photo.timestamp || photo.addedAt)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="photo-panel__actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewPhoto(photo);
                    }}
                    title="View Hi-Res"
                    className="photo-panel__action-btn photo-panel__action-btn--view"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                  {onAddPhotoAtLocation && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddPhotoAtLocation(photo.coordinates);
                      }}
                      title="Add Photo Here"
                      className="photo-panel__action-btn photo-panel__action-btn--add"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v8M8 12h8" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditPhoto(photo);
                    }}
                    title="Edit"
                    className="photo-panel__action-btn"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePhoto(photo.id);
                    }}
                    title="Delete"
                    className="photo-panel__action-btn photo-panel__action-btn--delete"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
